import React, { useEffect } from "react";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  TextField,
  Box,
  Checkbox,
  FormControlLabel,
  Typography,
  Button,
  IconButton,
  Divider,
  CircularProgress,
} from "@mui/material";
import FormDialog from "@components/FormDialog";
import { useTranslation } from "react-i18next";
import { X, Plus } from "lucide-react";
import { useSnackbar } from "@context/SnackBarProvider";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  createDriver,
  DriverFormData,
  driverSchema,
} from "@lib/api/mutations/driver";
import { AxiosError } from "axios";
import { DriverResponse } from "@lib/types/driver";
// import { handleError } from "@lib/utils/errorHandling";
import useHandleError from "@lib/utils/errorHook";
import { getUnusedSlots } from "@lib/api/queries/drivers";

import Autocomplete from "@mui/material/Autocomplete";
import Loading from "@components/Loading";

interface Props {
  addDriverDialogOpen: boolean;
  setAddDriverDialogOpen: (open: boolean) => void;
  refetch?: () => void;
}

const CreateDriver: React.FC<Props> = ({
  addDriverDialogOpen,
  setAddDriverDialogOpen,
  refetch,
}) => {
  const { t } = useTranslation();
  const { showSnackbar } = useSnackbar();
  const handleError = useHandleError();

  const {
    data: slots,
    isFetching,
    refetch: refetchSlots,
    error,
  } = useQuery<string[]>({
    queryKey: ["unused-slots"],
    queryFn: () => getUnusedSlots(),
    enabled: addDriverDialogOpen,
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<DriverFormData>({
    resolver: zodResolver(driverSchema),
    defaultValues: {
      name: "",
      code: "",
      description: "",
      phone: "",
      mobile_key: "",
      parameter_number: "",
      exclusive: false,
      custom_fields: [],
    },
  });

  // Field Array for dynamic custom fields
  const { fields, append, remove } = useFieldArray({
    control,
    name: "custom_fields",
  });

  const { mutate, isPending: isCreatingDriver } = useMutation({
    mutationFn: createDriver,
    onSuccess: (data: DriverResponse) => {
      showSnackbar(t("user.drivers.addSuccess"), "success");

      setAddDriverDialogOpen(false);
      reset();
      refetch && refetch();
    },
    onError: (error: AxiosError | unknown) => {
      if (error instanceof AxiosError && error.response) {
        const errorDetails = handleError(error.response.data);
        errorDetails && showSnackbar(errorDetails.message, "error");
      } else {
        showSnackbar(t("general.error"), "error");
      }
    },
  });

  const handleAddDriver = (data: DriverFormData) => {
    mutate(data);
  };

  return (
    <FormDialog
      title={t("user.drivers.add")}
      open={addDriverDialogOpen}
      onClose={() => {
        setAddDriverDialogOpen(false);
        reset();
      }}
      onSubmit={handleSubmit(handleAddDriver)}
      submitText={t("general.save")}
      isSubmitting={isCreatingDriver}
      submittingText={t("general.saving")}
    >
      {isFetching && <Loading />}
      {!isFetching && (
        <form noValidate>
          <Box display="flex" flexDirection="column" gap={2} py={2}>
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t("user.drivers.name")}
                  variant="outlined"
                  fullWidth
                  error={!!errors.name}
                  helperText={errors.name?.message}
                />
              )}
            />
            <Controller
              name="code"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t("user.drivers.code")}
                  variant="outlined"
                  fullWidth
                  error={!!errors.code}
                  helperText={errors.code?.message}
                />
              )}
            />
            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t("user.drivers.description")}
                  variant="outlined"
                  fullWidth
                  error={!!errors.description}
                  helperText={errors.description?.message}
                />
              )}
            />

            <Controller
              name="phone"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t("user.drivers.phone")}
                  variant="outlined"
                  fullWidth
                  error={!!errors.phone}
                  helperText={errors.phone?.message}
                />
              )}
            />

            <Controller
              name="mobile_key"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t("user.drivers.mobileKey")}
                  variant="outlined"
                  fullWidth
                  error={!!errors.mobile_key}
                  helperText={errors.mobile_key?.message}
                />
              )}
            />

            <Controller
              name="parameter_number"
              control={control}
              render={({ field }) => (
                <div>
                  <Autocomplete
                    {...field} // Spread the field props from react-hook-form
                    options={slots || ["30000"]} // Items to display in the dropdown
                    onChange={(_, value) => field.onChange(value)} // Handle the change
                    loading={isFetching} // Loading state
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t("user.drivers.parameterNumber")}
                        variant="outlined"
                        error={!!errors.parameter_number} // Error state for validation
                        helperText={errors.parameter_number?.message} // Error message
                      />
                    )}
                  />
                </div>
              )}
            />

            <Controller
              name="exclusive"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  label="Exclusive"
                  control={
                    <Checkbox {...field} className="hover:bg-transparent" />
                  }
                  className="text-sm"
                />
              )}
            />
            <Divider />
            {/* Custom Fields Section */}
            <Box>
              <Typography variant="subtitle1">
                {t("user.drivers.customFields")}
              </Typography>
              {fields.map((field, index) => (
                <Box
                  key={field.id}
                  display="flex"
                  gap={2}
                  alignItems="center"
                  my={1}
                >
                  <Controller
                    name={`custom_fields.${index}.key`}
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label={t("user.drivers.customFieldKey")}
                        variant="outlined"
                        fullWidth
                        error={!!errors.custom_fields?.[index]?.key}
                        helperText={errors.custom_fields?.[index]?.key?.message}
                      />
                    )}
                  />
                  <Controller
                    name={`custom_fields.${index}.value`}
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label={t("user.drivers.customFieldValue")}
                        variant="outlined"
                        fullWidth
                        error={!!errors.custom_fields?.[index]?.value}
                        helperText={
                          errors.custom_fields?.[index]?.value?.message
                        }
                      />
                    )}
                  />
                  <IconButton onClick={() => remove(index)} color="error">
                    <X />
                  </IconButton>
                </Box>
              ))}
              <Button
                variant="outlined"
                startIcon={<Plus />}
                onClick={() => append({ key: "", value: "" })}
              >
                {t("user.drivers.addCustomField")}
              </Button>
            </Box>
          </Box>
        </form>
      )}
    </FormDialog>
  );
};

export default CreateDriver;
